/**
 * ProfessocialPosting stellt alle Module zur Verfügung, die zum Posten in allen Netzwerken nötig sind
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { Grid, Paper, Stack, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { getGeneralPostingOptions, getUser, namedRequestData, } from '../store/common/common.selectors'
import { PostingScreen, PostPlaning, Restrictions } from '../shared/types/types'
import Posting from '../shared/components/ModuleComponents/Posting'
import PostingPreview from '../shared/components/ModuleComponents/PostingPreview'
import ImageVideo from '../shared/components/ModuleComponents/AddOns/ImageVideo/ImageVideo'
import Emotion from '../shared/components/ModuleComponents/AddOns/Emotion'
import Template from '../shared/components/ModuleComponents/AddOns/Template'

import HomeIcon from '@mui/icons-material/Home'
import EventIcon from '@mui/icons-material/Event'

import {
  confirmManualDeferredPostInitiated,
  createPostTemplateInitiated,
  getAllTagsInitiated,
  getDeferredPostByIdtInitiated,
  getTopicsAndMetadataForCurrentUserInitiated,
  listMediaFilesInitiated,
  publishPostInitiated,
  setUpdateTemplateId,
  updateDeferredPostInitiated,
  updatePagePostInitiated,
  updatePostTemplateInitiated,
} from '../store/posting/posting.actions'

import { EventType, Message, Severity } from '@professocial/professocialTypes/types/messageTypes'
import { PostingConfiguration } from '@professocial/professocialTypes/types/configurationTypes'
import {
  ActionCall,
  GeneralPostingOptions,
  PostingTemplate,
  PreviewFormat,
  SocialMediaNetwork
} from '@professocial/professocialTypes/types/commonTypes'

import {
  ConfirmManualDeferredPostRequest,
  CreatePostRequestBody,
  CreatePostTemplateRequest,
  DeferredPost,
  DeferredPostStatus,
  GetDeferredPostByIdRequest,
  ListMediaFilesRequest,
  MediaFileMetadata,
  MediaFileSorting,
  NetworkMap,
  NetworkType,
  PostContent,
  PostContentUpdateElement,
  PostTemplate,
  PostTemplateUpdateElement,
  PublishPostRequest,
  UpdateDeferredPostRequest,
  UpdatePagePostRequest,
  UpdatePostTemplateRequest
} from '@3m5/sm_publish-frontend-api'

import {
  clearRequests,
  setGeneralPostingOptions,
  setMessage,
  setPostingConfiguration,
  setUser,
} from '../store/common/common.actions'
import {
  getMediaData,
  getSingleDeferredPost,
  getUpdatedPostTemplate,
  getUpdateTemplateId
} from '../store/posting/posting.selector'
import clearTemplate from '../util/clearTemplate'
import validatePosting from '../util/validatePosting'
import Tags from '../shared/components/ModuleComponents/AddOns/Tags'
import { useHistory, useLocation } from 'react-router-dom'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import { PostingRootState } from '../store/postingRoot.reducer'
import { setTimeZone } from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import addTimezone from '../util/addTimezone'
import { useParams } from 'react-router'
import { roundToTwoDecimalPlaces } from '../util/checkRatio'

export interface IPostingProps {
  postingConfiguration?: PostingConfiguration,
  postingScreen?: PostingScreen,
  showTemplateInitState?: boolean,
}

export enum PostingActionType {
  CREATE_TEMPLATE = 'CREATE_TEMPLATE',
  UPDATE_TEMPLATE = 'UPDATE_TEMPLATE',
  CREATE_POSTING = 'CREATE_POSTING',
  CHANGE_POSTING = 'CHANGE_POSTING',
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(5),
  },
  button: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body2.fontSize,
    textTransform: 'initial',
  },
}))

export const mapToPostingTemplate = (post?: any, deferredPost?: DeferredPost): PostingTemplate | null => {
  let postingTemplate: PostingTemplate | null = null
  if (post) {
    postingTemplate = {
      content: post.postContent,
      aboTopicId: post.topicId,
      tags: post.tags,
      postStart: post.postStart,
      postEnd: post.postEnd ? new Date(setTimeZone(post.postEnd.getTime())) : undefined,
      manipulateLinks: post.linkIndividualizationAllowed,
      addHashtag: post.hashtagIndividualizationAllowed,
      socialMediaNetwork: {
        facebook: (post.allowedNetworks && !!post.allowedNetworks.find((elem: NetworkType) => elem === NetworkType.Facebook)) || (post.networkType && post.networkType === NetworkType.Facebook),
        instagram: (post.allowedNetworks && !!post.allowedNetworks.find((elem: NetworkType) => elem === NetworkType.Instagram)) || (post.networkType && post.networkType === NetworkType.Instagram),
        linkedIn: (post.allowedNetworks && !!post.allowedNetworks.find((elem: NetworkType) => elem === NetworkType.LinkedIn)) || (post.networkType && post.networkType === NetworkType.LinkedIn),
        googleMyBusiness: (post.allowedNetworks && !!post.allowedNetworks.find((elem: NetworkType) => elem === NetworkType.GoogleMyBusiness)) || (post.networkType && post.networkType === NetworkType.GoogleMyBusiness),
      },
    }
  }
  if (deferredPost && deferredPost.overridingPostContent) {
    postingTemplate = {
      deferredPostId: deferredPost.id,
      status: deferredPost.status,
      content: deferredPost.overridingPostContent,
      // aboTopicId: post.topicId,
      // tags: post.tags,
      postStart: deferredPost.postStart,
      postEnd: deferredPost.postEnd,
      // manipulateLinks: post.linkIndividualizationAllowed,
      // addHashtag: post.hashtagIndividualizationAllowed,
      socialMediaNetwork: {
        facebook: deferredPost.networkType === NetworkType.Facebook,
        instagram: deferredPost.networkType === NetworkType.Instagram,
        linkedIn: deferredPost.networkType === NetworkType.LinkedIn,
        googleMyBusiness: deferredPost.networkType === NetworkType.GoogleMyBusiness,
      },
    }
  }
  return postingTemplate
}

const ProfessocialPosting: React.FC<IPostingProps> = (props) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const location = useLocation<{ detail: any } >()
  const history = useHistory()

  const { deferredPostId } = useParams<{ deferredPostId: string }>()
  const ref = useRef<HTMLDivElement>(null)

  const storedFacebookPageId: string | null = window.localStorage.getItem('facebookPageNetworkId')
  const storedInstagramPageId: string | null = window.localStorage.getItem('instagramPageNetworkId')
  const storedGoogleMyBusinessPageId: string | null = window.localStorage.getItem('googleMyBusinessPageNetworkId')

  const generalPostingOptions: GeneralPostingOptions = useSelector(getGeneralPostingOptions)
  const user = useSelector(getUser)
  const classes = useStyles()

  const dispatchGetTags = useCallback(() => dispatch(getAllTagsInitiated()), [dispatch])
  const dispatchGetAboTopics = useCallback(() => dispatch(getTopicsAndMetadataForCurrentUserInitiated()), [dispatch])
  const dispatchPublishPost = useCallback((params: PublishPostRequest) => dispatch(publishPostInitiated(params)), [dispatch])
  const dispatchUpdatePagePost = useCallback((params: UpdatePagePostRequest) => dispatch(updatePagePostInitiated(params)), [dispatch])
  const dispatchGetSingleDeferredPost = useCallback((params: GetDeferredPostByIdRequest) => dispatch(getDeferredPostByIdtInitiated(params)), [dispatch])
  const dispatchUpdateDeferredPost = useCallback((params: UpdateDeferredPostRequest) => dispatch(updateDeferredPostInitiated(params)), [dispatch])
  const dispatchConfirmManualDeferredPost = useCallback((params: ConfirmManualDeferredPostRequest) => dispatch(confirmManualDeferredPostInitiated(params)), [dispatch])
  const dispatchCreatePostTemplate = useCallback((params: CreatePostTemplateRequest) => dispatch(createPostTemplateInitiated(params)), [dispatch])
  const dispatchMediaData = useCallback((params: ListMediaFilesRequest) => dispatch(listMediaFilesInitiated(params)), [dispatch])
  const dispatchSetMessage = useCallback((message: Message) => dispatch(setMessage(message)), [dispatch])
  const dispatchUpdatePostTemplate = useCallback((props: UpdatePostTemplateRequest) => dispatch(updatePostTemplateInitiated(props)), [dispatch])
  const dispatchSetTemplateID = useCallback((props: string) => dispatch(setUpdateTemplateId(props)), [dispatch])
  const dispatchSetUser = useCallback((props: any) => dispatch(setUser(props)), [dispatch])
  const dispatchClearRequests = useCallback(() => dispatch(clearRequests()), [dispatch])
  const dispatchSetGeneralPostingOptions = useCallback((params: GeneralPostingOptions) => dispatch(setGeneralPostingOptions(params)), [dispatch])
  const dispatchSetPostingConfiguration = useCallback((params: PostingConfiguration) => dispatch(setPostingConfiguration(params)), [dispatch])

  const mediaData = useSelector(getMediaData)
  const templateID: string | undefined = useSelector(getUpdateTemplateId)
  const updatedPostTemplate: PostTemplate | undefined = useSelector(getUpdatedPostTemplate)
  const singleDeferredPost: DeferredPost | undefined = useSelector(getSingleDeferredPost)
  const actionOnPublishPost: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, publishPostInitiated.type))
  const actionOnCreateTemplate: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, createPostTemplateInitiated.type))
  const actionOnUpdateTemplate: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, updatePostTemplateInitiated.type))
  const actionOnManualConfirm: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, confirmManualDeferredPostInitiated.type))
  const actionOnGetDeferredPostById: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, getDeferredPostByIdtInitiated.type))

  const [allowedSocialMediaNetworks, setAllowedSocialMediaNetworks] = useState<SocialMediaNetwork>(props.postingConfiguration?.licensePermissions || {})
  const [showPostApprovalDialog, setShowPostApprovalDialog] = useState<boolean>(false)
  const [alreadyConfirmed, setAlreadyConfirmed] = useState<boolean>(false)

  useEffect(() => {
    setAllowedSocialMediaNetworks(props.postingConfiguration?.licensePermissions || {})
  }, [props.postingConfiguration?.licensePermissions],
  )

  // todo restrictions vom  Server holen
  const restrictions: Restrictions = {
    facebook: {
      image: {
        maxFileSizeInBytes: 8000000,
        minAspectRatio: roundToTwoDecimalPlaces(9, 16),
        maxAspectRatio: roundToTwoDecimalPlaces(16, 9),
      },
    },
    instagram: {
      image: {
        maxFileSizeInBytes: 8000000,
        minResolution: {
          width: 320,
        },
        maxResolution: {
          width: 1440,
        },
        minAspectRatio: roundToTwoDecimalPlaces(4, 5),
        maxAspectRatio: 1.91,
      },
      video: {
        allowedFileExtensions: ['mov', 'mp4'],
        maxFileSizeInBytes: 100000000,
        minDurationInSeconds: 3,
        maxDurationInSeconds: 60,
        minAspectRatio: roundToTwoDecimalPlaces(4, 5),
        maxAspectRatio: roundToTwoDecimalPlaces(16, 9),
      },
    },
    googleMyBusiness: {
      image: {
        maxFileSizeInBytes: 5000000,
        minResolution: {
          width: 250,
        },
      },
      video: {
        minDurationInSeconds: 3,
        maxDurationInSeconds: 30,
        maxFileSizeInBytes: 75000000,
      },
    },
  }

  const defaultPosting: PostingTemplate = {
    content: {
      text: undefined,
      link: undefined,
      video: undefined,
      image: undefined,
      headline: undefined,
      extensions: {},
    },
    manipulateLinks: true,
    socialMediaNetwork: props.postingConfiguration?.licensePermissions || {},
  }
  const [posting, setPosting] = useState<PostingTemplate>(defaultPosting)
  const [originTemplate, setOriginTemplate] = useState<PostTemplate | undefined>()
  const [postingScreen, setPostingScreen] = useState<PostingScreen>(props.postingScreen ?? PostingScreen.MAIN)
  const [activeTab, setActiveTab] = React.useState<NetworkType>(NetworkType.Facebook)
  const [networkPageId, setNetworkPageId] = React.useState<string>(props.postingConfiguration?.networkPage?.facebookPage?.networkPageId || '')
  const showPreview = props.postingConfiguration?.generalPostingOptions.showPreview || false

  const [date, setDate] = useState<PostPlaning>({ start: null, end: null })

  const handlePostPublishDate = (span: PostPlaning) => {
    setDate(span)
    setPosting({
      ...posting,
      postStart: span.start || undefined,
      postEnd: span.end || undefined,
    })
  }

  useEffect(() => {
    let isCurrent = true
    if (isCurrent) {
      setPosting(defaultPosting)
    }

    return () => {
      isCurrent = false
    }
  }, [user])

  useEffect(() => {
    deferredPostId && dispatchGetSingleDeferredPost({ deferredPostId })
  }, [deferredPostId])

  useEffect(() => {
    singleDeferredPost && handlePostingChange(undefined, singleDeferredPost)
  }, [singleDeferredPost])

  useEffect(() => {
    actionOnGetDeferredPostById?.error?.status === 404 && setAlreadyConfirmed(true)
  }, [actionOnGetDeferredPostById])

  useEffect(() => {
    props.postingConfiguration?.networkPage?.facebookPage && setNetworkPageId(props.postingConfiguration.networkPage.facebookPage.networkPageId)
  }, [props.postingConfiguration?.networkPage])

  useEffect(() => {
    // Todo noch spezifizieren, wer den Dialog bekommt und wer nicht > AGMB ausblenden (momentan inplizit über das popupFlag)
    if (!props.postingConfiguration?.generalPostingOptions.showAsPopUp) {
      if (actionOnPublishPost?.status === 'success' ||
          actionOnUpdateTemplate?.status === 'success' ||
          actionOnManualConfirm?.status === 'success' ||
          actionOnCreateTemplate?.status === 'success') {
        setShowPostApprovalDialog(true)
        dispatchClearRequests()
      }
    } else {
      setPosting(defaultPosting)
      dispatchClearRequests()
    }
  }, [actionOnPublishPost, actionOnCreateTemplate, actionOnUpdateTemplate, actionOnManualConfirm],
  )

  useEffect(() => {
    props.postingConfiguration && dispatchSetPostingConfiguration(props.postingConfiguration)
    props.postingConfiguration?.user && dispatchSetUser(props.postingConfiguration?.user)
    props.postingConfiguration?.generalPostingOptions && dispatchSetGeneralPostingOptions(props.postingConfiguration?.generalPostingOptions)
    props.postingConfiguration?.templateId && dispatchSetTemplateID(props.postingConfiguration.templateId)
    props.postingConfiguration?.post && setPosting(props.postingConfiguration?.post)
    props.postingConfiguration?.post && setDate({
      start: addTimezone(props.postingConfiguration?.post.postStart) || undefined,
      end: addTimezone(props.postingConfiguration?.post.postEnd) || undefined,
    })
  }, [props.postingConfiguration],
  )

  useEffect(() => {
    dispatchGetAboTopics()
    dispatchGetTags()

    if (location?.state?.detail) {
      handlePostingChange(location.state.detail.data)
      dispatchSetTemplateID(location.state.detail.data.id)
      location.state.detail.role === 'EDITOR' && props.postingConfiguration?.generalPostingOptions &&
        dispatchSetGeneralPostingOptions({
          ...props.postingConfiguration?.generalPostingOptions,
          allowTemplateEdit: true,
        })
      history.replace({ state: {} })
    }
  }, [],
  )

  useEffect(() => {
    if (postingScreen === PostingScreen.MEDIA && !mediaData) {
      dispatchMediaData({
        limit: 40,
        sort: MediaFileSorting.UploadedAt,
        sortDesc: true,
        onlyEditorFiles: user?.role === 'EDITOR',
      })
    }
  }, [postingScreen],
  )

  useEffect(() => {
    ref && ref.current?.scrollIntoView({ behavior: 'smooth' })
  }, [ref, postingScreen])

  const comparePosts = (origin: PostContent, post: PostContent) => {
    let comparison = true
    if (JSON.stringify(origin.link) !== JSON.stringify(post.link)) {
      comparison = false
    }
    if (JSON.stringify(origin.image) !== JSON.stringify(post.image)) {
      comparison = false
    }
    if (JSON.stringify(origin.video) !== JSON.stringify(post.video)) {
      comparison = false
    }
    return comparison
  }

  const checkDates = (origin: PostTemplate, publishDate: Date) => {
    return ((origin.postStart && origin.postStart <= publishDate) || !origin.postStart) && ((origin.postEnd && origin.postEnd >= publishDate) || !origin.postEnd)
  }

  function handleUpdateDeferredPost(id: string, posting: PostingTemplate) {

    const content: PostContentUpdateElement = {
      headline: posting.content.headline,
      text: posting.content.text || '',
      link: posting.content.link,
      extensions: posting.content.extensions,
    }

    if (posting.content.image?.mediaId) {
      content.image = {
        mediaId: posting.content.image?.mediaId,
        description: posting.content.image?.description,
      }
    }

    if (posting.content.video?.mediaId) {
      content.video = {
        mediaId: posting.content.video?.mediaId,
        description: posting.content.video?.description,
      }
    }

    let postStart = posting.postStart

    // Prüfen ob start in der Vergangenheit?
    if (postStart && new Date(postStart).getTime() <= new Date().getTime()) {
      postStart = undefined
    }

    const params: UpdateDeferredPostRequest = {
      deferredPostId: id,
      deferredPostUpdateElement: {
        postStart: postStart,
        postEnd: posting.postEnd,
        postContent: content,
      },
    }

    try {
      const result = dispatchUpdateDeferredPost(params)
    } catch (error) {
      console.error('DEBUG - Error calling dispatchUpdateDeferredPost:', error)
    }
  }

  const sendPosting = (action: PostingActionType) => {
    const validatedPostingContent = posting.content
    if (validatedPostingContent.image || validatedPostingContent.video) {
      delete validatedPostingContent.link
    }
    const createdPostingContent: PostContent = {
      ...validatedPostingContent,
      extensions: validatedPostingContent.extensions || {},
      text: posting.content.text ? posting.content.text.trim() : '',
    }
    const publishDate = date.start !== null ? date.start : (action === PostingActionType.CREATE_POSTING ? undefined : new Date())
    const postEndDate = date.end !== null ? date.end : undefined

    let createPosting: CreatePostRequestBody | undefined = {
      postTemplateId: originTemplate?.id,
      overridingPostContent: createdPostingContent,
      postStart: publishDate,
      postEnd: postEndDate,
      networkMaps: [],
    }

    // 1. Message prüfen ob gesetzt
    if (!createdPostingContent.text) {
      dispatchSetMessage({ message: t('toast:postingMessageError'), severity: Severity.error, eventType: EventType.create })
      createPosting = undefined
    }
    // 2. Template gewählt und Änderungen im Content?
    const samePost = originTemplate ? comparePosts(originTemplate.postContent, createdPostingContent) : false
    // 2.a) Template gewählt und unverändert (samePost = true)

    // Datum prüfen, ob innerhalb der Template-Datums-Grenzen
    if (originTemplate && !checkDates(originTemplate, publishDate || new Date())) {
      createPosting = undefined
    }

    // message prüfen und ggf. overridingPostContent entfernen
    if (createPosting && originTemplate?.postContent.text === createdPostingContent.text) {
      createPosting.overridingPostContent = undefined
    }

    // 2.b) kein Template gewählt oder der Post wurde maßgeblich verändert (mehr als message) > neuer Post
    if (!samePost && createPosting) {
      createPosting.postTemplateId = undefined
    }

    // 2. socialMediaNetwork prüfen > Instagram hat weitere Restriktionen
    const networkMaps: NetworkMap[] = []
    if (posting.socialMediaNetwork.facebook && (props.postingConfiguration?.networkPage?.facebookPage?.networkPageId || storedFacebookPageId)) {
      networkMaps.push({
        networkType: NetworkType.Facebook,
        pages: [props.postingConfiguration?.networkPage?.facebookPage?.networkPageId || storedFacebookPageId || ''],
      })
    }
    if (posting.socialMediaNetwork.instagram && (props.postingConfiguration?.networkPage?.instagramPage?.networkPageId || storedInstagramPageId)) {
      networkMaps.push({
        networkType: NetworkType.Instagram,
        pages: [props.postingConfiguration?.networkPage?.instagramPage?.networkPageId || storedInstagramPageId || ''],
      })
    }
    // TODO make dynamic
    if (posting.socialMediaNetwork.linkedIn) {
      networkMaps.push({
        networkType: NetworkType.LinkedIn,
        pages: ['102250856'],
      })
    }
    if (posting.socialMediaNetwork.googleMyBusiness && (props.postingConfiguration?.networkPage?.googleMyBusinessPage?.networkPageId || storedGoogleMyBusinessPageId)) {
      networkMaps.push({
        networkType: NetworkType.GoogleMyBusiness,
        pages: [props.postingConfiguration?.networkPage?.googleMyBusinessPage?.networkPageId || storedGoogleMyBusinessPageId || ''],
      })
    }

    // Posting validieren
    const errors = validatePosting(posting, restrictions, generalPostingOptions)

    // wenn Posting verifiziert ist, absenden
    if (createPosting && errors.length === 0) {
      // networkMaps setzen
      createPosting.networkMaps = networkMaps

      // Posting data
      const data: PublishPostRequest = {
        createPostRequestBody: createPosting,
      }

      // Template data
      const allowedNetworks: NetworkType[] = []
      if (posting.socialMediaNetwork.facebook) {
        allowedNetworks.push(NetworkType.Facebook)
      }
      if (posting.socialMediaNetwork.instagram) {
        allowedNetworks.push(NetworkType.Instagram)
      }
      if (posting.socialMediaNetwork.linkedIn) {
        allowedNetworks.push(NetworkType.LinkedIn)
      }
      if (posting.socialMediaNetwork.googleMyBusiness) {
        allowedNetworks.push(NetworkType.GoogleMyBusiness)
      }

      const templateContent: PostContentUpdateElement = {
        text: createdPostingContent.text || '',
        extensions: {},
      }
      if (createdPostingContent.link?.url) {
        templateContent.link = { url: createdPostingContent.link?.url }
      }
      if (createdPostingContent.image?.mediaId) {
        templateContent.image = { mediaId: createdPostingContent.image?.mediaId, description: createdPostingContent.image?.description }
      }
      if (createdPostingContent.video?.mediaId) {
        templateContent.video = { mediaId: createdPostingContent.video?.mediaId, description: createdPostingContent.video?.description }
      }
      if (createdPostingContent.extensions.googleMyBusinessExtension?.ctaActionType && createdPostingContent.extensions.googleMyBusinessExtension?.ctaLink) {
        templateContent.extensions.googleMyBusinessExtension = {
          ctaActionType: createdPostingContent.extensions.googleMyBusinessExtension?.ctaActionType,
          ctaLink: createdPostingContent.extensions.googleMyBusinessExtension?.ctaLink,
        }
      }
      const postTemplate: PostTemplateUpdateElement = {
        postContent: templateContent,
        active: true,
        postEnd: postEndDate,
        postStart: publishDate,
        linkIndividualizationAllowed: posting.manipulateLinks || false,
        hashtagIndividualizationAllowed: posting.addHashtag || false,
        topicId: posting.aboTopicId,
        tags: posting.tags || [],
        allowedNetworks: allowedNetworks,
      }

      const requestTemplate: CreatePostTemplateRequest = {
        postTemplateUpdateElement: postTemplate,
      }

      let updateTemplate: UpdatePostTemplateRequest | undefined
      if (templateID) updateTemplate = { templateId: templateID, postTemplateUpdateElement: postTemplate }
      switch (action) {
      case PostingActionType.CREATE_TEMPLATE:
        // templates werden vom Editor erstellt
        dispatchCreatePostTemplate(requestTemplate)
        break
      case PostingActionType.UPDATE_TEMPLATE:
        // Templates werden nur vom Editor bearbeitet
        updateTemplate && dispatchUpdatePostTemplate(updateTemplate)
        break
      case PostingActionType.CREATE_POSTING:
        if (((deferredPostId && (singleDeferredPost?.status === DeferredPostStatus.AwaitingUserConfirmation || singleDeferredPost?.status === DeferredPostStatus.NoActiveUsersForPage))
          || (posting.deferredPostId && (posting.status === DeferredPostStatus.AwaitingUserConfirmation || posting.status === DeferredPostStatus.NoActiveUsersForPage))) 
          && !alreadyConfirmed) {
          const id = deferredPostId || posting.deferredPostId

          // manueller deferred post akzeptieren
          if (id) {
            try {
              handleUpdateDeferredPost(id, posting)
            } catch (error) {
              console.error('DEBUG - Error calling handleUpdateDeferredPost:', error)
            }

            try {
              dispatchConfirmManualDeferredPost({ deferredPostId: id })
            } catch (error) {
              console.error('DEBUG - Error calling dispatchConfirmManualDeferredPost:', error)
            }
          } else {
            console.log('DEBUG - No valid id found for update and confirm')
          }
        } else {
          // postings werden vom user erstellt
          dispatchPublishPost(data)
        }
        break
      case PostingActionType.CHANGE_POSTING:
        // postings werden vom user geändert

        // TODO für Facebook und Instagram ggf. erweitern
        if (posting.pagePostId && posting.socialMediaNetwork.googleMyBusiness && props.postingConfiguration?.networkUserId) {
          // GoogleMyBusiness
          const content: PostContentUpdateElement = {
            headline: posting.content.headline,
            text: posting.content.text || '',
            link: posting.content.link,
            extensions: posting.content.extensions,
          }
          if (posting.content.image?.mediaId) {
            content.image = {
              mediaId: posting.content.image?.mediaId,
              description: posting.content.image?.description,
            }
          }
          if (posting.content.video?.mediaId) {
            content.video = {
              mediaId: posting.content.video?.mediaId,
              description: posting.content.video?.description,
            }
          }
          const params: UpdatePagePostRequest = {
            networkUserId: props.postingConfiguration?.networkUserId,
            networkPageId: props.postingConfiguration?.networkPage?.googleMyBusinessPage?.networkPageId || storedGoogleMyBusinessPageId || '',
            networkPostId: posting.pagePostId,
            postContentUpdateElement: content,
          }
          dispatchUpdatePagePost(params)
        }
        if (posting.deferredPostId) {
          handleUpdateDeferredPost(posting.deferredPostId, posting)
          if (posting.status === DeferredPostStatus.NoActiveUsersForPage && !alreadyConfirmed) {
            dispatchConfirmManualDeferredPost({ deferredPostId: posting.deferredPostId })
          }
        }
        break
      }
    }

    // Todo addOns
    // a) emotion
    // b) location
  }

  const handleActiveTab = (tab:NetworkType) => {
    setActiveTab(tab)
    let networkId = ''
    switch (tab) {
    case NetworkType.Facebook:
      networkId = props.postingConfiguration?.networkPage?.facebookPage?.networkPageId || ''
      break
    case NetworkType.Instagram:
      networkId = props.postingConfiguration?.networkPage?.instagramPage?.networkPageId || ''
      break
    case NetworkType.LinkedIn:
      networkId = props.postingConfiguration?.networkPage?.linkedInPage?.networkPageId || ''
      break
    case NetworkType.GoogleMyBusiness:
      networkId = props.postingConfiguration?.networkPage?.googleMyBusinessPage?.networkPageId || ''
      break
    }
    setNetworkPageId(networkId)
  }

  const handlePostingChange = (post?: PostTemplate, deferredPost?: DeferredPost) => {
    const templatePosting: PostingTemplate | null = mapToPostingTemplate(post, deferredPost)

    if (templatePosting) {
      handlePostPublishDate({ start: templatePosting.postStart as Date | null, end: templatePosting.postEnd as Date | null })
      setPosting(clearTemplate(templatePosting))
    }
    setOriginTemplate(post)
  }

  const isSocialMediaNetworkActive = posting.socialMediaNetwork.facebook || posting.socialMediaNetwork.instagram || posting.socialMediaNetwork.linkedIn
  const buttonActionGroup =
    <Stack spacing={3} sx={{ p: 2 }}>
      <Stack direction='row' spacing={2}>
        <ButtonComponent
          variant='contained'
          text={user?.role === 'EDITOR' ? t('postingCommon:createNewTemplate') : t('postingCommon:createNewPost')}
          onClick={() => {
            setPosting(defaultPosting)
            setDate({ start: null, end: null })
            setShowPostApprovalDialog(false)
          }}
        />
        {user?.role === 'EDITOR' &&
          <ButtonComponent
            variant='outlined'
            text={user?.role === 'EDITOR' ? t('postingCommon:editFurther') : t('postingCommon:editPostFurther')}
            onClick={() => {
              setShowPostApprovalDialog(false)
            }}
          />}
      </Stack>
      <Stack>
        <ButtonComponent
          variant='text'
          color='info'
          className={classes.button}
          startIcon={<HomeIcon />}
          text={t('postingCommon:publishToDashboard')}
          onClick={() => {
            setShowPostApprovalDialog(false)
            history.push({
              pathname: `/${user?.role.toLowerCase()}/dashboard/networkContent`,
              state: { detail: { templateId: updatedPostTemplate?.id } },
            })
          }}
        />
        <ButtonComponent
          variant='text'
          color='info'
          className={classes.button}
          startIcon={<EventIcon />}
          text={t('postingCommon:publishToCalender')}
          onClick={() => {
            // handlePublishAction()
            setShowPostApprovalDialog(false)
            history.push({
              pathname: `/${user?.role.toLowerCase()}/dashboard/calendar`,
              state: { detail: { templateId: updatedPostTemplate?.id } },
            })
          }}
        />
      </Stack>
    </Stack>

  return (
    <>
      <Grid
        ref={ref}
        sx={{ scrollMarginTop: 130 }} // header height
        container
        direction='row'
      >
        <Grid container item xs={12}>
          {postingScreen === PostingScreen.MAIN &&
            <>
              <Grid item xs={12}>
                <Posting
                  previewFormat={props.postingConfiguration?.generalPostingOptions.previewFormat || PreviewFormat.square}
                  allowedSocialMediaNetworks={allowedSocialMediaNetworks}
                  restrictions={restrictions}
                  posting={posting}
                  date={date}
                  sendAction={(action: PostingActionType) => {
                    sendPosting(action)
                  }}
                  changePublishDate={handlePostPublishDate}
                  changePosting={(post: PostingTemplate) => {
                    post && setPosting({
                      ...post,
                      postStart: date.start || undefined,
                      postEnd: date.end || undefined,
                    })

                    if (!post.socialMediaNetwork[activeTab.charAt(0).toLowerCase() + activeTab.slice(1)]) {
                      Object.keys(post.socialMediaNetwork).some((elem) => {
                        // set the first Letter of a string Uppercase
                        const smNetwork = (elem.charAt(0).toUpperCase() + elem.slice(1)) as NetworkType

                        if (post.socialMediaNetwork[elem.charAt(0).toLowerCase() + elem.slice(1)]) {
                          smNetwork !== NetworkType.GoogleMyBusiness && handleActiveTab(smNetwork)
                          return true
                        }
                        return false
                      })
                    }
                  }}
                  changePostingScreen={(screen: PostingScreen) => { setPostingScreen(screen) }}
                />
              </Grid>
              {isSocialMediaNetworkActive && showPreview && (
                <Paper className={classes.paper}>
                  <Grid item xs={12}>
                    <PostingPreview
                      user={user || props.postingConfiguration?.user}
                      posting={posting}
                      networkPageId={networkPageId}
                      activeItem={activeTab}
                      onChangeTab={handleActiveTab}
                    />
                  </Grid>
                </Paper>
              )}
            </>}
          {postingScreen === PostingScreen.MEDIA &&
            <>
              <Grid item xs={12}>
                <ImageVideo
                  restrictions={restrictions}
                  posting={posting}
                  changePosting={(post: PostingTemplate) => {
                    post && setPosting(post)
                  }}
                  previewFormat={props.postingConfiguration?.generalPostingOptions.previewFormat || PreviewFormat.square}
                  useMediaGallery={props.postingConfiguration?.generalPostingOptions.useMediaGallery}
                  changePostingScreen={(screen: PostingScreen) => { setPostingScreen(screen) }}
                />
              </Grid>
            </>}
          {postingScreen === PostingScreen.EMOTION &&
            <>
              <Grid item xs={12}>
                <Emotion
                  posting={posting}
                  changePosting={(post: PostingTemplate) => {
                    post && setPosting(post)
                  }}
                  changePostingScreen={(screen: PostingScreen) => { setPostingScreen(screen) }}
                />
              </Grid>
            </>}
          {/*{postingScreen === PostingScreen.LOCATION &&*/}
          {/*  <>*/}
          {/*    <Grid item xs={12}>*/}
          {/*      <Location*/}
          {/*        posting={posting}*/}
          {/*        open={postingScreen === PostingScreen.LOCATION}*/}
          {/*        changePosting={(post: PostingTemplate) => {*/}
          {/*          post && setPosting(post)*/}
          {/*        }}*/}
          {/*        changePostingScreen={(screen: PostingScreen) => { setPostingScreen(screen) }}*/}
          {/*      />*/}
          {/*    </Grid>*/}
          {/*  </>}*/}
          {postingScreen === PostingScreen.TAGS &&
            <>
              <Grid item xs={12}>
                <Tags
                  posting={posting}
                  changePosting={(post: PostingTemplate) => {
                    post && setPosting(post)
                  }}
                  changePostingScreen={(screen: PostingScreen) => { setPostingScreen(screen) }}
                />
              </Grid>
            </>}
          {postingScreen === PostingScreen.TEMPLATE &&
            <>
              <Grid item xs={12}>
                <Template
                  user={user || props.postingConfiguration?.user}
                  changePosting={handlePostingChange}
                  changePostingScreen={(screen: PostingScreen) => { setPostingScreen(screen) }}
                  showTemplateInitState={props.showTemplateInitState}
                />
              </Grid>
            </>}
        </Grid>
      </Grid>
      <DialogComponent
        open={showPostApprovalDialog}
        autoClose={false}
        content={buttonActionGroup}
      />
    </>
  )
}
export default ProfessocialPosting
